import type { Profile } from '@/types';

/** Navigation entries. Labels are product copy and therefore pt-BR; paths follow the same wording. */
export interface NavigationItem {
  readonly to: string;
  readonly label: string;
  /** Decorative glyph; the label carries the meaning. */
  readonly icon: string;
  /** Matches the route exactly instead of by prefix — used for the index route. */
  readonly end?: boolean | undefined;
}

export const NAVIGATION_ITEMS = {
  catalog: { to: '/catalogo', label: 'Catálogo', icon: '▦' },
  cart: { to: '/carrinho', label: 'Carrinho', icon: '▤' },
  orders: { to: '/pedidos', label: 'Pedidos', icon: '▧' },
  contracts: { to: '/contratos', label: 'Contratos', icon: '▣' },
  payments: { to: '/pagamentos', label: 'Pagamentos', icon: '◧' },
  tracking: { to: '/rastreamento', label: 'Rastreamento', icon: '◎' },
  backoffice: { to: '/backoffice', label: 'Backoffice', icon: '▩' },
  organization: { to: '/minha-organizacao', label: 'Minha organização', icon: '◈' },
} as const satisfies Record<string, NavigationItem>;

export type NavigationItemId = keyof typeof NAVIGATION_ITEMS;

/** pt-BR name of each profile, for the topbar and for permission messages. */
export const PROFILE_LABELS: Readonly<Record<Profile, string>> = {
  operator: 'Operador',
  buyer: 'Comprador',
  commercial_backoffice: 'Backoffice comercial',
  procurement: 'Compras',
  controllership: 'Controladoria',
  platform_admin: 'Administrador da plataforma',
};

/**
 * The single mapping from a profile to what it can reach. Every screen that needs to know which
 * profiles may open it reads this file rather than re-listing profiles inline, so adding a
 * profile or a module is one edit.
 *
 * Hiding a link is a usability affordance, not the security control: the profile claim is
 * re-verified by the API on every request and Row Level Security filters the rows in the
 * database, so a user who types the URL of a module they cannot reach still gets nothing.
 */
export const PROFILE_NAVIGATION: Readonly<Record<Profile, readonly NavigationItemId[]>> = {
  // `contracts` follows `orders` for exactly the profiles `TenantSessionDep,
  // require_profiles(OPERATOR, BUYER, PLATFORM_ADMIN)` admits on `/v1/contracts/*`
  // (SPRINT08.md, "Agreed HTTP contract") — the same set the term-checkout path itself reads.
  operator: ['catalog', 'cart', 'orders', 'contracts', 'tracking', 'organization'],
  buyer: ['catalog', 'cart', 'orders', 'contracts', 'payments', 'tracking', 'organization'],
  commercial_backoffice: ['orders', 'payments', 'tracking', 'backoffice', 'organization'],
  procurement: ['catalog', 'orders', 'backoffice', 'organization'],
  controllership: ['orders', 'payments', 'backoffice', 'organization'],
  // Holds every internal permission (ADR-0015), so it reaches every internal area.
  platform_admin: ['catalog', 'orders', 'contracts', 'payments', 'backoffice', 'organization'],
};

/** Entries the given profile may see. An absent or unrecognised profile reaches nothing. */
export function navigationForProfile(profile: Profile | null): readonly NavigationItem[] {
  if (profile === null) {
    return [];
  }

  return PROFILE_NAVIGATION[profile].map((id) => NAVIGATION_ITEMS[id]);
}

/** Profiles allowed on a given navigation entry — the inverse of `PROFILE_NAVIGATION`. */
export function profilesForNavigationItem(id: NavigationItemId): readonly Profile[] {
  return (Object.keys(PROFILE_NAVIGATION) as Profile[]).filter((profile) =>
    PROFILE_NAVIGATION[profile].includes(id),
  );
}

/** Where the shell draws the entries a profile can reach. */
export interface NavigationLayout {
  /** Daily destinations, drawn inline in the top bar. */
  readonly primary: readonly NavigationItem[];
  /** Account-level or occasional pages, drawn in the account menu. */
  readonly menu: readonly NavigationItem[];
}

/** Always behind the account menu: pages about the account, not about today's work. */
const MENU_ALWAYS: readonly NavigationItemId[] = ['contracts', 'organization'];

/**
 * Behind the account menu for a profile that buys: once the buying journey carries payment and
 * delivery inside the order itself, a separate top-level stop for each makes the customer choose
 * between three doors to the same order.
 */
const MENU_FOR_BUYERS: readonly NavigationItemId[] = ['payments', 'tracking'];

/**
 * Splits what `PROFILE_NAVIGATION` admits into the top bar and the account menu. Presentation
 * only: the route guards keep reading `PROFILE_NAVIGATION`, so moving an entry between the two
 * lists can never open or close a route.
 *
 * The cart has no entry of its own in either list — the top bar's cart pill is that entry — and a
 * profile that buys sees the catalogue named for what it does there ("Comprar").
 */
export function navigationLayoutForProfile(profile: Profile | null): NavigationLayout {
  if (profile === null) {
    return { primary: [], menu: [] };
  }

  const ids = PROFILE_NAVIGATION[profile];
  const buys = ids.includes('cart');
  const inMenu = (id: NavigationItemId) =>
    MENU_ALWAYS.includes(id) || (buys && MENU_FOR_BUYERS.includes(id));
  const draw = (id: NavigationItemId): NavigationItem =>
    buys && id === 'catalog'
      ? { ...NAVIGATION_ITEMS.catalog, label: 'Comprar' }
      : NAVIGATION_ITEMS[id];

  return {
    primary: ids.filter((id) => id !== 'cart' && !inMenu(id)).map(draw),
    menu: ids.filter((id) => id !== 'cart' && inMenu(id)).map(draw),
  };
}
